'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateLongShortFundingRateAndLiveTwaps,
	MarketStatus,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, MarketId } from '@velocity-exchange/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { useImmer } from 'use-immer';
import { getFundingRatePct } from 'src/utils/fundingRate';
import { useCurrentSlot } from 'src/hooks/useCurrentSlot';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useUiUpdateInterval from 'src/hooks/useUiUpdateInterval';
import {
	useLiveMarkPrice,
	useLiveMmOracle,
	useLiveOraclePrice,
} from './marketLiveStateCore';
import { useLivePerpMarketInfo } from './marketStatsCore';

/**
 * The display-formatted market-info a full market header renders: funding rate,
 * funding countdown, open interest, and live TWAPs, plus oracle slot-delays.
 *
 * This is the stage-4 home of what used to live inside `useMarketInfoDisplayData`.
 * Inputs come from module slices (prices from the price store, the perp market
 * account from the stats store) plus the chain-head slot from `useSlotQuery`, so
 * the hook is a derivation over shared state rather than a re-read of raw
 * velocityClient accounts. One sync hook runs this and publishes the result into
 * the store slice below for consumers to read (see `useMarketInfoDisplayData`).
 */
export type MarketData = {
	longFundingRate: string;
	shortFundingRate: string;
	estimatedFundingRateCountdownTs: number;
	maxOpenInterest: string;
	openInterest: string;
	longOpenInterest: string;
	shortOpenInterest: string;
	markTwapLive: string;
	oracleTwapLive: string;
	lastOracleSlotDelay: number;
	lastMmOracleSlotDelay: number;
	hasLoadedInitialData: boolean;
};

export const MARKET_DATA_BASE_STATE: MarketData = {
	longFundingRate: `0.00`,
	shortFundingRate: `0.00`,
	estimatedFundingRateCountdownTs: 0,
	maxOpenInterest: '0',
	openInterest: `0`,
	longOpenInterest: `0`,
	shortOpenInterest: `0`,
	markTwapLive: `0`,
	oracleTwapLive: `0`,
	lastOracleSlotDelay: 0,
	lastMmOracleSlotDelay: 0,
	hasLoadedInitialData: false,
};

const LATE_FUNDING_CUTOFF_PERIOD = 60 * 20;

// --- Shared-state slice for the derived display data -----------------------

/**
 * One producer (`useSyncMarketInfoDisplay`) writes the derivation's result here
 * and every consumer reads it back through `useLiveMarketInfoDisplayData`. A
 * plain store rather than the React Query cache, which would put two observers
 * for one key on the same component as the producer.
 */
interface MarketInfoDisplayStore {
	data: MarketData;
}

export const useCurrentMarketInfoDisplayStore = create<MarketInfoDisplayStore>(
	() => ({
		data: MARKET_DATA_BASE_STATE,
	})
);

/** Every field of `MarketData` is a primitive, so equal fields means equal data. */
const marketDataIsUnchanged = (a: MarketData, b: MarketData): boolean =>
	(Object.keys(a) as (keyof MarketData)[]).every((k) => a[k] === b[k]);

/**
 * Publish the derived display data. Producer only — see the sync hook.
 *
 * Gates on value, not reference: consumers read this on the same fiber the
 * producer writes it from, so republishing a fresh-but-unchanged object forces
 * them to re-render from inside the commit, and enough of those chained together
 * exceed React's nested-update limit.
 */
export const publishMarketInfoDisplay = (data: MarketData): void => {
	const prev = useCurrentMarketInfoDisplayStore.getState().data;
	if (marketDataIsUnchanged(prev, data)) return;

	useCurrentMarketInfoDisplayStore.setState({ data });
};

/** When the next funding payment is expected, in unix seconds. */
export const calculateNextFundingPaymentTs = (
	now: number,
	periodicityNum: number,
	lastFundingTsNum: number
) => {
	let nextFundingTs: number;

	// Determine when next funding is expected
	//// https://docs.velocity.exchange/funding-rates

	const periodStartForTs = (ts: number) => ts - (ts % periodicityNum);

	const nextFundingEpoch = periodStartForTs(now) + periodicityNum;

	const lastFundingWasLate =
		lastFundingTsNum % periodicityNum > LATE_FUNDING_CUTOFF_PERIOD;

	const lastFundingWasPaidDuringCurrentPeriod =
		periodStartForTs(lastFundingTsNum) === periodStartForTs(now);

	// Check if funding is due
	let fundingIsCurrentlyDue = false;

	//// Case 1 : Last funding was not late, but missed the following payment period
	if (!lastFundingWasLate && !lastFundingWasPaidDuringCurrentPeriod) {
		fundingIsCurrentlyDue = true;
	}

	//// Case 2 : Last funding was late, but missed the skipped payment period
	if (lastFundingWasLate && !lastFundingWasPaidDuringCurrentPeriod) {
		const nextFundingShouldBePaidAt =
			periodStartForTs(lastFundingTsNum) + periodicityNum * 2;

		if (nextFundingShouldBePaidAt <= now) {
			fundingIsCurrentlyDue = true;
		}
	}

	// # Set the expected next funding timestamp
	if (lastFundingWasLate && lastFundingWasPaidDuringCurrentPeriod) {
		nextFundingTs = nextFundingEpoch + periodicityNum;
	} else if (fundingIsCurrentlyDue) {
		nextFundingTs = periodStartForTs(now);
	} else {
		nextFundingTs = nextFundingEpoch;
	}

	return nextFundingTs;
};

/**
 * Derive the display-formatted market info for a market from live-state module
 * slices. Recomputes when the perp account or prices change, and when the
 * chain-head slot is re-sampled on the essential UI cadence (~1s) — not on every
 * Solana slot. Resets to base state while the client is disconnected so retained
 * slices aren't shown as live. Sourced from the module instead of raw
 * velocityClient reads.
 */
export const useLiveMarketInfoDisplay = (
	marketId: MarketId | undefined
): MarketData => {
	const marketIsPerp = marketId?.isPerp;
	const velocityClientIsReady = useVelocityClientIsReady();
	const perpAccount = useLivePerpMarketInfo(marketId);
	const markPrice = useLiveMarkPrice(marketId);
	const oracleLivePrice = useLiveOraclePrice(marketId);
	const mmOraclePriceData = useLiveMmOracle(marketId);

	// The chain-head slot advances every Solana slot (~400ms). Sample it at the
	// essential UI cadence (~1s) so this derivation — funding/OI/live-TWAPs and
	// the oracle slot-delays — refreshes on the documented slow cadence rather
	// than recomputing and republishing on every slot.
	const liveSlot = useCurrentSlot();
	const liveSlotRef = useRef(liveSlot);
	liveSlotRef.current = liveSlot;
	const [nowSlot, setNowSlot] = useState(liveSlot);
	const sampleSlot = useCallback(() => setNowSlot(liveSlotRef.current), []);
	useUiUpdateInterval(sampleSlot, true, false);

	const [marketData, setMarketData] = useImmer<MarketData>(
		MARKET_DATA_BASE_STATE
	);

	useEffect(() => {
		// When the client isn't connected the module slices retain their last
		// values, so reset to base rather than displaying stale funding/OI/TWAPs
		// as though they were live (mirrors the pre-migration reset behaviour).
		if (!velocityClientIsReady) {
			setMarketData(MARKET_DATA_BASE_STATE);
			return;
		}

		if (!marketId) return;

		const nowBN = new BN((Date.now() / 1000).toFixed(0));
		const now = nowBN.toNumber();

		const lastOracleSlot = oracleLivePrice?.slot ?? 0;
		const lastMmOracleSlot = mmOraclePriceData?.slot ?? 0;

		// Spot markets have no perp account: only the oracle slot-delays and
		// load flag are meaningful.
		if (!marketIsPerp) {
			setMarketData((s) => {
				s.hasLoadedInitialData = lastOracleSlot !== 0;
				s.lastOracleSlotDelay = nowSlot - lastOracleSlot;
				s.lastMmOracleSlotDelay = nowSlot - lastMmOracleSlot;
			});
			return;
		}

		const market = perpAccount;

		if (!market || ENUM_UTILS.match(market.status, MarketStatus.DELISTED)) {
			setMarketData(MARKET_DATA_BASE_STATE);
			return;
		}

		const lastFundingTs = market.lastFundingRateTs;
		const periodicity = market.marketStats.fundingPeriod;

		if (!lastFundingTs || !periodicity) {
			setMarketData(MARKET_DATA_BASE_STATE);
			return;
		}

		const periodicityNum = periodicity.toNumber();
		const lastFundingTsNum = lastFundingTs.toNumber();

		const nextFundingTs = calculateNextFundingPaymentTs(
			now,
			periodicityNum,
			lastFundingTsNum
		);
		const nextFundingTimeRemaining = Math.max(nextFundingTs - now, 0);

		setMarketData((s) => {
			s.maxOpenInterest = BigNum.from(
				market.maxOpenInterest,
				BASE_PRECISION_EXP
			).toMillified();
			s.openInterest = BigNum.from(
				market.baseAssetAmountLong.add(market.baseAssetAmountShort.abs()),
				BASE_PRECISION_EXP
			).toMillified();
			s.longOpenInterest = BigNum.from(
				market.baseAssetAmountLong,
				BASE_PRECISION_EXP
			).toMillified();
			s.shortOpenInterest = BigNum.from(
				market.baseAssetAmountShort.abs(),
				BASE_PRECISION_EXP
			).toMillified();

			if (nowSlot > 0) {
				s.lastOracleSlotDelay = nowSlot - lastOracleSlot;
				s.lastMmOracleSlotDelay = nowSlot - lastMmOracleSlot;
			}

			s.estimatedFundingRateCountdownTs = nextFundingTimeRemaining;
		});

		if (!oracleLivePrice) {
			setMarketData((s) => {
				s.hasLoadedInitialData = true;
			});
			return;
		}

		// The SDK reads only `.price` from these args at runtime (and the mmOracle
		// arg is unused entirely while `markPrice` is supplied), so pass the
		// reactive prices as `{ price }` — same reactive cadence as `markPrice`,
		// no imperative raw needed. `oracleLivePrice` is guaranteed defined past
		// the guard above; `mmOraclePriceData` may be absent (transports that
		// don't publish an mmOracle), so its price/confidence fall back to ZERO.
		// The oracle `{ price }` satisfies OraclePriceData directly; only the
		// mmOracle arg needs suppression (MMOraclePriceData requires `isMMOracleActive`).
		const [markTwapLive, oracleTwapLive, longFundingRate, shortFundingRate] =
			calculateLongShortFundingRateAndLiveTwaps(
				market,
				// @ts-expect-error - we only need price and confidence from mmOraclePriceData. once SDK it updated to > v0.6.2, this can be removed.
				{
					price: mmOraclePriceData?.price?.val ?? ZERO,
					confidence: mmOraclePriceData?.confidence ?? ZERO,
				},
				{ price: oracleLivePrice.price.val },
				markPrice?.val ?? ZERO,
				nowBN
			);

		setMarketData((s) => {
			s.markTwapLive = BigNum.from(markTwapLive, QUOTE_PRECISION_EXP).toFixed(
				3
			);
			s.oracleTwapLive = BigNum.from(
				oracleTwapLive,
				QUOTE_PRECISION_EXP
			).toFixed(3);
			s.longFundingRate = getFundingRatePct(longFundingRate).printShort();
			s.shortFundingRate = getFundingRatePct(shortFundingRate).printShort();

			s.hasLoadedInitialData = true;
		});
	}, [
		velocityClientIsReady,
		marketIsPerp,
		marketId?.key,
		markPrice?.toNum(),
		perpAccount,
		mmOraclePriceData?.slot,
		oracleLivePrice?.slot,
		nowSlot,
	]);

	return marketData;
};
